/**
 * VP FlightsHotel + fixed "+" markup: compose PWCC never includes agent extra
 * commission, and selected-room PWCC can still omit it (stale fares / 3-room
 * search where handleFee is stamped on rooms but not rolled into PWCC).
 * Add the missing amount so sidebar סה״כ לתשלום moves with עמלה כללית or עמלה לחדר.
 */

function round2(n) {
  return Math.round(Number(n) * 100) / 100;
}

function asNonNeg(n) {
  const v = Number(n);
  return Number.isFinite(v) && v > 0 ? v : 0;
}

export function vacationPackageOrderLevelFixedMarkupIls(handleFeeData) {
  if (!handleFeeData || handleFeeData.type !== "+") return 0;
  return round2(asNonNeg(handleFeeData.value));
}

/**
 * True when selected-room PWCC does not already contain room-level handle fees.
 * PWCC with fee is ~ (PWC + handleFee) * (1 + cc%); without fee it stays below PWC + handleFee.
 */
export function isVpRoomHandleFeeMissingFromPwcc({
  summedRoomPwccIls,
  roomsBaseTotal,
  roomLevelHandleFeesIls,
} = {}) {
  const roomFees = round2(asNonNeg(roomLevelHandleFeesIls));
  if (roomFees <= 0.01) return false;
  const roomPwcc = round2(asNonNeg(summedRoomPwccIls));
  const pwcSum = round2(asNonNeg(roomsBaseTotal));
  return roomPwcc + 0.5 < pwcSum + roomFees;
}

/**
 * @param {{
 *   basePwccIls: number,
 *   usedCompose: boolean,
 *   summedRoomPwccIls?: number,
 *   roomsBaseTotal?: number,
 *   roomLevelHandleFeesIls?: number,
 *   configuredRoomMarkupIls?: number,
 *   hasManualRoomMarkup?: boolean,
 *   handleFeeData?: { type?: string, value?: number }|null,
 *   existingCreditCardFee?: number,
 *   baggageFare?: number,
 *   tripGuarantyPrice?: number,
 *   creditCardFeePercent?: number,
 * }} args
 * @returns {{
 *   finalTotalIls: number,
 *   handleFee: number,
 *   creditCardFee: number,
 *   extraMarkupIls: number,
 *   extraCreditCardFeeIls: number,
 *   auditStep: string,
 * }}
 */
export function resolveVpFixedMarkupPayableTotal({
  basePwccIls,
  usedCompose,
  summedRoomPwccIls = 0,
  roomsBaseTotal = 0,
  roomLevelHandleFeesIls = 0,
  configuredRoomMarkupIls = 0,
  hasManualRoomMarkup = false,
  handleFeeData = null,
  existingCreditCardFee = 0,
  baggageFare = 0,
  tripGuarantyPrice = 0,
  creditCardFeePercent = 0,
} = {}) {
  const orderMarkup = vacationPackageOrderLevelFixedMarkupIls(handleFeeData);
  const roomFees = round2(asNonNeg(roomLevelHandleFeesIls));
  const configured = round2(asNonNeg(configuredRoomMarkupIls));
  // עמלה לחדר (manual) is the slot sum; עמלה כללית is a single order amount (not × rooms).
  const expectedMarkup = hasManualRoomMarkup
    ? round2(Math.max(configured, orderMarkup))
    : orderMarkup;
  const base = round2(asNonNeg(basePwccIls));
  const feeMissingFromRoomPwcc = isVpRoomHandleFeeMissingFromPwcc({
    summedRoomPwccIls,
    roomsBaseTotal,
    roomLevelHandleFeesIls: Math.max(roomFees, expectedMarkup),
  });

  const roomsHaveNoFee = roomFees <= 0.01;
  const stampedFeeBelowExpected =
    expectedMarkup > 0.01 && roomFees + 0.5 < expectedMarkup;
  const shouldAddExpectedMarkup =
    expectedMarkup > 0.01 &&
    (usedCompose === true ||
      roomsHaveNoFee ||
      feeMissingFromRoomPwcc ||
      stampedFeeBelowExpected);

  let extraMarkupIls = 0;
  let handleFee = roomFees;
  let auditStep = usedCompose
    ? "vp_fixed_markup:compose_pwcc_incomplete_selection"
    : "vp_fixed_markup:sum_room_pwcc_plus_baggage";

  if (shouldAddExpectedMarkup) {
    extraMarkupIls = expectedMarkup;
    handleFee = extraMarkupIls;
    auditStep = usedCompose
      ? hasManualRoomMarkup
        ? "vp_fixed_markup:compose_pwcc_plus_room_handle_fee"
        : "vp_fixed_markup:compose_pwcc_plus_order_handle_fee"
      : "vp_fixed_markup:sum_room_pwcc_plus_missing_handle_fee";
  }

  const pct = Number(creditCardFeePercent);
  const extraCreditCardFeeIls =
    extraMarkupIls > 0 && Number.isFinite(pct) && pct > 0
      ? round2((extraMarkupIls * pct) / 100)
      : 0;

  const creditCardFee = round2(
    asNonNeg(existingCreditCardFee) + extraCreditCardFeeIls
  );
  const finalTotalIls = round2(
    base +
      extraMarkupIls +
      extraCreditCardFeeIls +
      asNonNeg(baggageFare) +
      asNonNeg(tripGuarantyPrice)
  );

  return {
    finalTotalIls,
    handleFee: round2(handleFee),
    creditCardFee,
    extraMarkupIls: round2(extraMarkupIls),
    extraCreditCardFeeIls,
    auditStep,
  };
}
